import React, { useState, useEffect } from "react";
import UserTable from "./UserTable";
import UserForm from "./UserForm";
import UserManagementHeader from "./UserManagementHeader";
import {
  apiService,
  authService,
  UserManagement as ApiUser,
} from "../../services";
import { rolesService } from "../../services/roles";

interface User {
  id: string;
  name: string;
  email: string;
  phone: string;
  role: string;
  role_name?: string;
  status: "active" | "inactive";
  createdAt: string;
  password?: string;
}

// Global cache for users data
let globalUsersCache: User[] | null = null;
let globalDataLoaded = false;

const UserManagement: React.FC = () => {
  const [users, setUsers] = useState<User[]>(globalUsersCache || []);
  const [isLoading, setIsLoading] = useState(!globalDataLoaded);
  const [error, setError] = useState<string>("");
  const [apiErrors, setApiErrors] = useState<Record<string, string>>({});
  const [currentUserId, setCurrentUserId] = useState<string>("");
  const [dataLoaded, setDataLoaded] = useState(globalDataLoaded);

  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterRole, setFilterRole] = useState("all");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);

  // Fetch users from API
  const fetchUsers = async (forceReload: boolean = false) => {
    // If data is already loaded and not forcing reload, skip
    if (dataLoaded && !forceReload) {
      return;
    }

    try {
      setIsLoading(true);
      setError("");
      const response = await apiService.getUsers();

      // Convert API data to local User format
      const convertedUsers: User[] = response.data.map((apiUser: ApiUser) => ({
        id: apiUser._id,
        name: apiUser.name,
        email: apiUser.email,
        phone: apiUser.phone,
        role: apiUser.role_id, // Store the role ID for form submission
        role_name: apiUser.role_name,
        status: "active" as const, // Default to active since API doesn't have status
        createdAt: apiUser.createdAt,
      }));

      setUsers(convertedUsers);
      setDataLoaded(true);

      // Update global cache
      globalUsersCache = convertedUsers;
      globalDataLoaded = true;
    } catch (error: any) {
      console.error("Failed to fetch users:", error);
      setError(error.message || "Không thể tải danh sách người dùng");
    } finally {
      setIsLoading(false);
    }
  };

  // Load users on component mount only if not already loaded
  useEffect(() => {
    if (!dataLoaded) {
      fetchUsers();
    }
  }, [dataLoaded]);

  // Get current user ID
  useEffect(() => {
    const getCurrentUser = async () => {
      try {
        const user = await authService.getCurrentUser();
        if (user) {
          setCurrentUserId(user._id);
        }
      } catch (error) {
        console.error("Failed to get current user:", error);
      }
    };
    getCurrentUser();
  }, []);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const handleCreateNew = () => {
    setEditingUser(null);
    setShowForm(true);
    setError(""); // Clear any previous error messages
    setApiErrors({}); // Clear any previous API errors
  };

  const handleEdit = (userId: string) => {
    const user = users.find((u) => u.id === userId);
    if (user) {
      setEditingUser(user);
      setShowForm(true);
      setError(""); // Clear any previous error messages
      setApiErrors({}); // Clear any previous API errors
    }
  };

  const handleDelete = (userId: string) => {
    // Prevent deleting current user
    if (currentUserId === userId) {
      return;
    }

    const user = users.find((u) => u.id === userId);
    if (user) {
      setUserToDelete(user);
      setShowDeleteModal(true);
    }
  };

  const confirmDelete = async () => {
    if (userToDelete) {
      try {
        await apiService.deleteUser(userToDelete.id);
        console.log("User deleted successfully");

        // Refresh users list after successful deletion
        await fetchUsers(true); // Force reload after delete
        setShowDeleteModal(false);
        setUserToDelete(null);
      } catch (error: any) {
        console.error("Failed to delete user:", error);
        setError(error.message || "Không thể xóa người dùng");
      }
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setUserToDelete(null);
  };

  const handleSave = async (
    userData: Omit<User, "id" | "createdAt"> & { confirmPassword?: string }
  ) => {
    try {
      if (editingUser) {
        // Update existing user
        const updateData: any = {
          name: userData.name,
          email: userData.email,
          phone: userData.phone,
          role_id: userData.role, // userData.role now contains the actual role ID
        };

        // Only include password if it's provided (not empty)
        if (userData.password && userData.password.trim() !== "") {
          updateData.password = userData.password;
        }

        await apiService.updateUser(editingUser.id, updateData);
        console.log("User updated successfully");
      } else {
        // Create new user
        const createData = {
          name: userData.name,
          email: userData.email,
          phone: userData.phone,
          password: userData.password || "123456",
          role_id: userData.role, // userData.role now contains the actual role ID
        };

        await apiService.createUser(createData);
        console.log("User created successfully");
      }

      // Refresh users list after successful operation
      await fetchUsers(true); // Force reload after create/update
      setShowForm(false);
      setEditingUser(null);
    } catch (error: any) {
      console.error("Failed to save user:", error);
      console.log("Error object:", error);
      console.log("Error.errors:", error.errors);

      // Handle validation errors from API
      if (error.errors && Array.isArray(error.errors)) {
        const fieldErrors: Record<string, string> = {};

        error.errors.forEach((err: any) => {
          const fieldName = err.path || err.field || "unknown";
          let message = err.msg || err.message || "Lỗi không xác định";

          // Translate common validation messages
          const messageTranslations: Record<string, string> = {
            "Please provide a valid phone number": "Số điện thoại không hợp lệ",
            "Phone number is required": "Số điện thoại là bắt buộc",
            "Invalid phone format": "Định dạng số điện thoại không hợp lệ",
            "Email is required": "Email là bắt buộc",
            "Invalid email format": "Định dạng email không hợp lệ",
            "Name is required": "Tên là bắt buộc",
            "Password is required": "Mật khẩu là bắt buộc",
            "Password too short": "Mật khẩu quá ngắn",
            "Email already exists": "Email đã được sử dụng",
            "Phone already exists": "Số điện thoại đã được sử dụng",
          };

          message = messageTranslations[message] || message;
          fieldErrors[fieldName] = message;
        });

        setApiErrors(fieldErrors);
        setError(""); // Clear general error
      } else {
        setError(error.message || "Không thể lưu thông tin người dùng");
        setApiErrors({}); // Clear field errors
      }
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingUser(null);
    setError(""); // Clear any error messages when canceling
    setApiErrors({}); // Clear API errors when canceling
  };

  // Function to reset data loaded flag (useful when navigating back to this page)
  const resetDataLoaded = () => {
    setDataLoaded(false);
    globalDataLoaded = false;
    globalUsersCache = null;
  };

  // Filter users based on search and filters
  const filteredUsers = users.filter((user) => {
    const matchesSearch =
      user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.phone.includes(searchTerm);

    const matchesRole =
      filterRole === "all" ||
      (filterRole === "admin" && user.role_name === "Admin") ||
      (filterRole === "user" && user.role_name === "User");

    return matchesSearch && matchesRole;
  });

  // Calculate pagination
  const totalPages = Math.ceil(filteredUsers.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const currentUsers = filteredUsers.slice(startIndex, endIndex);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleRowsPerPageChange = (rows: number) => {
    setRowsPerPage(rows);
    setCurrentPage(1);
  };

  if (showForm) {
    return (
      <div>
        <UserForm
          user={editingUser}
          onSave={handleSave}
          onCancel={handleCancel}
          isEditing={!!editingUser}
          apiErrors={apiErrors}
        />
      </div>
    );
  }

  // Show loading state only if data hasn't been loaded yet and we're actually loading
  if (isLoading && !dataLoaded && users.length === 0) {
    return (
      <div>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-600 dark:text-gray-300">
            Đang tải danh sách người dùng...
          </span>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div>
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          <div className="whitespace-pre-line">{error}</div>
        </div>
        <button
          onClick={() => fetchUsers(true)}
          className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
        >
          Thử lại
        </button>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <UserManagementHeader onCreateNew={handleCreateNew} />

      {/* Loading indicator for background refresh */}
      {isLoading && dataLoaded && (
        <div className="mb-4 flex items-center justify-center">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
          <span className="ml-2 text-sm text-gray-600 dark:text-gray-300">
            Đang cập nhật...
          </span>
        </div>
      )}

      {/* Search Bar */}
      <div className="mb-4">
        <div className="flex items-center space-x-4">
          <div className="flex-1 max-w-2xl relative">
            <input
              type="text"
              placeholder="Tìm kiếm theo tên, email hoặc SĐT..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 pl-10 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg
                className="h-5 w-5 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-6">
        <div className="flex items-center space-x-4">
          <select
            value={filterRole}
            onChange={(e) => setFilterRole(e.target.value)}
            className="px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            <option value="all">Tất cả vai trò</option>
            <option value="admin">Quản trị viên</option>
            <option value="user">Người dùng</option>
          </select>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <div className="bg-white dark:bg-gray-800 px-4 py-4 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
          <div className="flex items-center">
            <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
              <svg
                className="w-6 h-6 text-blue-600 dark:text-blue-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-300">
                Tổng người dùng
              </p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{users.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 px-4 py-4 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
          <div className="flex items-center">
            <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-lg">
              <svg
                className="w-6 h-6 text-green-600 dark:text-green-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-300">Người dùng</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {users.filter((u) => u.role_name === "User").length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 px-4 py-4 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
          <div className="flex items-center">
            <div className="p-3 bg-red-100 dark:bg-red-900/30 rounded-lg">
              <svg
                className="w-6 h-6 text-red-600 dark:text-red-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7zM9 9h.01M15 9h.01"
                />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-300">Quản trị viên</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {users.filter((u) => u.role_name === "Admin").length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* User Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                <th className="w-1/5 px-2 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider border-b border-gray-200 dark:border-gray-700">
                  Tên người dùng
                </th>
                <th className="w-1/5 px-2 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider border-b border-gray-200 dark:border-gray-700">
                  Email
                </th>
                <th className="w-1/5 px-2 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider border-b border-gray-200 dark:border-gray-700">
                  Số điện thoại
                </th>
                <th className="w-1/6 px-2 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider border-b border-gray-200 dark:border-gray-700">
                  Vai trò
                </th>
                <th className="w-1/6 px-2 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider border-b border-gray-200 dark:border-gray-700">
                  Ngày tạo
                </th>
                <th className="w-1/5 px-2 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider border-b border-gray-200 dark:border-gray-700">
                  Thao tác
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
              {currentUsers.length > 0 ? (
                currentUsers.map((user) => (
                  <tr
                    key={user.id}
                    className="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                  >
                    <td className="w-1/5 px-2 py-4">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-8 w-8">
                          <div className="h-8 w-8 rounded-full bg-blue-600 flex items-center justify-center">
                            <span className="text-xs font-medium text-white">
                              {user.name.charAt(0).toUpperCase()}
                            </span>
                          </div>
                        </div>
                        <div className="ml-3 min-w-0 flex-1">
                          <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                            {user.name}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="w-1/4 px-2 py-4">
                      <div className="text-sm text-gray-900 dark:text-white truncate">
                        {user.email}
                      </div>
                    </td>
                    <td className="w-1/6 px-2 py-4">
                      <div className="text-sm text-gray-900 dark:text-white">{user.phone}</div>
                    </td>
                    <td className="w-1/6 px-2 py-4">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          user.role_name === "Admin"
                            ? "bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300"
                            : "bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300"
                        }`}
                      >
                        {rolesService.getRoleDisplayName(
                          user.role_name || "User"
                        )}
                      </span>
                    </td>
                    <td className="w-1/6 px-2 py-4">
                      <div className="text-sm text-gray-900 dark:text-white">
                        {new Date(user.createdAt).toLocaleDateString("vi-VN")}
                      </div>
                    </td>
                    <td className="w-1/6 px-2 py-4 text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => handleEdit(user.id)}
                          className="p-2 text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-md transition-colors"
                          title="Chỉnh sửa"
                        >
                          <svg
                            className="w-5 h-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                            />
                          </svg>
                        </button>
                        <button
                          onClick={() => handleDelete(user.id)}
                          disabled={currentUserId === user.id}
                          className={`p-2 rounded-md transition-colors ${
                            currentUserId === user.id
                              ? "text-gray-300 cursor-not-allowed"
                              : "text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/30"
                          }`}
                          title={
                            currentUserId === user.id
                              ? "Không thể xóa chính mình"
                              : "Xóa"
                          }
                        >
                          <svg
                            className="w-5 h-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                            />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={6}
                    className="px-2 py-12 text-center text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-900"
                  >
                    <div className="flex flex-col items-center">
                      <svg
                        className="w-12 h-12 text-gray-300 dark:text-gray-600 mb-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"
                        />
                      </svg>
                      <p className="text-lg font-medium text-gray-600 mb-2">
                        Không tìm thấy người dùng
                      </p>
                      <p className="text-sm text-gray-400 dark:text-gray-500">
                        Không có người dùng nào phù hợp với bộ lọc hiện tại.
                      </p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Footer */}
        {filteredUsers.length > 0 && (
          <div className="bg-white dark:bg-gray-800 px-4 py-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-700 dark:text-gray-300">Hiển thị</span>
              <select
                value={rowsPerPage}
                onChange={(e) =>
                  handleRowsPerPageChange(Number(e.target.value))
                }
                className="border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value={5}>5 dòng</option>
                <option value={10}>10 dòng</option>
                <option value={25}>25 dòng</option>
                <option value={50}>50 dòng</option>
              </select>
            </div>

            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-700 dark:text-gray-300">
                Hiển thị {startIndex + 1}-
                {Math.min(endIndex, filteredUsers.length)} trong{" "}
                {filteredUsers.length} kết quả
              </span>

              <div className="flex items-center space-x-1">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  &lt;
                </button>

                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum: number;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }

                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      className={`px-3 py-1 text-sm border rounded transition-colors ${
                        currentPage === pageNum
                          ? "bg-blue-600 dark:bg-blue-500 text-white border-blue-600 dark:border-blue-500"
                          : "border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600"
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}

                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  &gt;
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && userToDelete && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 dark:bg-black dark:bg-opacity-70 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
          <div className="relative mx-auto p-5 border border-gray-200 dark:border-gray-700 w-96 shadow-lg rounded-md bg-white dark:bg-gray-800">
            <div className="mt-3 text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 dark:bg-red-900/30">
                <svg
                  className="h-6 w-6 text-red-600 dark:text-red-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mt-4">
                Xác nhận xóa
              </h3>
              <div className="mt-2 px-4 py-3">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Bạn có chắc chắn muốn xóa người dùng{" "}
                  <span className="font-medium text-gray-900 dark:text-white">
                    {userToDelete.name}
                  </span>{" "}
                  không? Hành động này không thể hoàn tác.
                </p>
              </div>
              <div className="flex justify-center space-x-4 mt-4">
                <button
                  onClick={cancelDelete}
                  className="px-4 py-2 bg-gray-300 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-400 dark:hover:bg-gray-600 transition-colors"
                >
                  Hủy
                </button>
                <button
                  onClick={confirmDelete}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 dark:hover:bg-red-500 transition-colors"
                >
                  Xóa
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
